import React, { useEffect } from 'react';
import { Activity } from '@ids-ts/loader';
import { authInit, isPreprodEnv } from './AuthenticationInit';
import styles from './Authentication.css';

export interface AuthenticationProps {
  /**
   * @title Layout
   * @default vertical
   */
  layout: 'horizontal' | 'vertical';
  /**
   * @title Asset Alias
   * @default Intuit.cg.ttcom
   */
  assetAlias:
    | 'Intuit.cg.ttcom'
    | 'Intuit.accounting.core.qbowebapp'
    | 'Intuit.smallbusiness.qbse'
    | 'Intuit.platform.msc'
    | 'Intuit.qbshared.tsheets'
    | 'Intuit.sbg-fms.ippdevx';
  /**
   * @title App Group
   * @default TurboTax
   */
  group:
    | 'TurboTax'
    | 'QBO'
    | 'QBSE'
    | 'MerchantServiceCenter'
    | 'QBTime'
    | 'ExternalDeveloperPortal';
  /**
   * @title Show Learn More Link
   * @description If true, the link will be shown. The link itself is set in IUX code
   * @default false
   */
  showLearnMoreLink?: boolean;
  /**
   * @TJS-ignore
   */
  offeringZone?: 'tax' | 'ssz';
  /**
   * @TJS-ignore
   */
  theme?: 'turbotax' | 'quickbooks';
  /**
   * @TJS-ignore
   */
  learnMoreURL?: string;
  /**
   * @TJS-ignore
   */
  accountRecoveryLink?: string;
  /**
   * @TJS-ignore
   */
  testCookieName?: string;
  /**
   * @title Element attributes
   * @description Add element attributes such as data- or id to the container element.
   */
  attributes?: {
    [key: string]: string;
  };
}

const MYTT_DOMAIN_E2E = 'cqa.myturbotax.intuit.com';
const MYTT_DOMAIN_PROD = 'myturbotax.intuit.com';
const IUX_DOMAIN_E2E = 'accounts-e2e.intuit.com';
const IUX_DOMAIN_PROD = 'accounts-tax.intuit.com';
const IUX_SCRIPT_PATH = '/IUS-Plugins/v2/scripts/iux-core.js';

/**
 * Check whether an existing IUX script has loaded. If not, either wait for it to load, or load a new one.
 */
export const loadIuxScript = () =>
  new Promise((done) => {
    const existingScripts = Array.from(document.getElementsByTagName('script'));
    const hasExistingScript = existingScripts.find((elem) => {
      try {
        const src = new URL(elem.src || '');
        if (src.hostname !== IUX_DOMAIN_E2E && src.hostname !== IUX_DOMAIN_PROD) {
          return false;
        }
        return src.pathname === IUX_SCRIPT_PATH;
      } catch (err) {
        // Note: err is ignored here because there are legitmate script tags w/o valid urls
        // And, in the world case, if all script tag fails, this does not crash anything. Instead, a new script tag is added.
        return false;
      }
    });
    const hasIuxFullyLoaded = !!(window as any).intuit?.ius?.simpleSignIn;

    if (hasExistingScript && !hasIuxFullyLoaded) {
      window.addEventListener('DOMContentLoaded', done);
    } else if (!hasExistingScript) {
      const script = document.createElement('script');
      const iuxDomain =
        isPreprodEnv(window.location.host) === true ? IUX_DOMAIN_E2E : IUX_DOMAIN_PROD;
      script.src = `https://${iuxDomain}${IUX_SCRIPT_PATH}`;
      script.onload = done;
      document.head.appendChild(script);
    } else {
      done(null);
    }
  });

/** A simple component. */
export const Authentication = (props: AuthenticationProps) => {
  const {
    layout = 'vertical',
    accountRecoveryLink = '',
    attributes = {},
    theme = 'turbotax',
    assetAlias = 'Intuit.cg.ttcom',
    offeringZone = 'tax',
    showLearnMoreLink,
  } = props;
  const isTurbotax =
    theme === 'turbotax' && assetAlias === 'Intuit.cg.ttcom' && offeringZone === 'tax';

  let { learnMoreURL = '' } = props;
  if (!learnMoreURL && showLearnMoreLink && isTurbotax) {
    learnMoreURL = 'https://ttlc.intuit.com/questions/3614066-creating-an-account';
  }

  useEffect(() => {
    if (document && window) {
      let signInLink = '';
      let signUpLink = '';
      const myttDomain =
        isPreprodEnv(window.location.host) === true ? MYTT_DOMAIN_E2E : MYTT_DOMAIN_PROD;
      if (!signInLink && isTurbotax) {
        signInLink = `https://${myttDomain}/`;
      }
      if (!signUpLink && isTurbotax) {
        signUpLink = `https://${myttDomain}/sign-up`;
      }

      loadIuxScript().then(() => {
        // do stuff with the script
        authInit(window, document, {
          layout,
          learnMoreURL,
          signUpLink,
          signInUrl: signInLink,
          accountRecoveryLink,
          theme,
          offeringId: assetAlias,
          offeringZone,
        });
      });
    }
  }, [isTurbotax, layout, learnMoreURL, accountRecoveryLink, theme, assetAlias, offeringZone]);
  return (
    <div
      {...attributes}
      className={styles.authContainer}
      data-com-id="Authentication"
      id="ius-simple-sign-in-widget"
      data-auth-widget-layout={layout}
      data-auth-learnmore-url={learnMoreURL}
    >
      <div className={styles.authLoadingIndicator}>
        <Activity shape="dots" size="large" />
      </div>
    </div>
  );
};

export default Authentication;
